import React from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Box, Copy, Flex } from '@elparking/components'

import { AVAILABLE, UNAVAILABLE } from 'commons/js/constants'
import { Events } from 'commons/js/services/analytics'
import ParkingLegend from 'commons/js/components/ParkingLegend'
import ParkingRestrictions from 'commons/js/components/ParkingRestrictions'
import { ServiceIconMeasures, Services, PARKING_HEIGTH, PARKING_LENGTH } from 'commons/js/components/ParkingServices'

import ParkingCardButton from './ParkingCardButton'
import Distance from './Distance'
import ParkingAirportService from './ParkingAirportService'
import { byDistanceAsc } from '../../util/parkingComparator'
import { isTruckParking } from '../../util/isTruckParking'
import HighlightedBlock from 'commons/js/ui/HighlightedBlock'

import {
    ParkingNameBlock,
    ParkingAddressBlock,
    ParkingTextBlock,
    Card,
    ButtonContainer,
    CardFooter,
    CardFooterForAirport,
    CardHeader,
    ParkingImageBlock,
    ImageParking,
    ParkingTextBlockHeader,
    ButtonPriceInactive,
    ButtonPrice,
} from './layout'

const MAX_SERVICES = 3
class ParkingCard extends React.PureComponent {
    static propTypes = {
        parking: PropTypes.shape({
            isAirport: PropTypes.bool,
            isActive: PropTypes.bool,
            isOfficial: PropTypes.bool,
            id: PropTypes.number,
            name: PropTypes.string,
            address: PropTypes.string,
            price: PropTypes.number,
            max_height: PropTypes.number,
            max_length: PropTypes.number,
            entrances: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                address: PropTypes.string,
                latitude: PropTypes.number,
                longitude: PropTypes.number,
            })),
            services: PropTypes.array,
            bookingRestriction: PropTypes.object,
            bookingAvailability: PropTypes.shape({
                price: PropTypes.number,
                availabilityStatus: PropTypes.string,
            }),
            bookingServices: PropTypes.arrayOf(PropTypes.shape({
                type: PropTypes.number,
                transferTime: PropTypes.string,
                transferTimeFrequency: PropTypes.string,
            })),
            typeString: PropTypes.string,
            longitude: PropTypes.number,
            latitude: PropTypes.number,
            media: PropTypes.shape({
                small: PropTypes.string,
            }),
        }),
        computeDistanceTo: PropTypes.func,
        buildParkingUrl: PropTypes.func,
    }

    static defaultProps = {
        computeDistanceTo: () => 0,
        buildParkingUrl: () => '',
    }

    getParkingImage (parking) {
        if (parking.isOfficial) {
            return '/images/parking/aena-photo.webp'
        }
        return parking.media.small || `/images/parking/default_gray_medium.webp`
    }

    renderRestrictions () {
        const { parking } = this.props
        const { bookingRestriction, bookingAvailability } = parking
        const { availabilityStatus } = bookingAvailability

        if (!bookingRestriction) {
            return null
        }

        if (availabilityStatus === AVAILABLE || availabilityStatus === UNAVAILABLE) {
            return null
        }

        return <ParkingRestrictions restrictions={bookingRestriction} />
    }

    renderAirportBlock () {
        const { bookingServices } = this.props.parking

        return <ParkingAirportService bookingServices={bookingServices} />
    }

    render () {
        const { parking, computeDistanceTo, buildParkingUrl } = this.props
        const { name, isAirport, isActive, isOfficial, entrances } = parking
        const [ nearestEntrance, ...otherEntrances ] = entrances.slice().sort(byDistanceAsc(computeDistanceTo))
        const otherEntrancesCount = otherEntrances.length
        const distance = computeDistanceTo(nearestEntrance.latitude, nearestEntrance.longitude)
        const url = buildParkingUrl(parking)
        const Footer = isAirport ? CardFooterForAirport : CardFooter
        const Button = isActive ? ButtonPrice : ButtonPriceInactive

        return (
            <Card unavailable={!isActive} dataTest={`parking-card-${parking.id}`} data-event={Events.parkingSearchItemSelect}>
                <a rel='noopener noreferrer' href={url} target='_blank'>
                    {this.renderRestrictions()}
                    <CardHeader>
                        <ParkingImageBlock>
                            <ImageParking background={this.getParkingImage(parking)} border={isOfficial} />
                        </ParkingImageBlock>
                        <ParkingTextBlock>
                            <ParkingTextBlockHeader>
                                <ParkingLegend parking={parking} />
                                <ParkingNameBlock>{name}</ParkingNameBlock>
                                <ParkingAddressBlock>
                                    <Box>
                                        <Copy as='span' padding='0' fontWeight='book' size='xSmall'>
                                            <FormattedMessage defaultMessage='Acceso por' />
                                        </Copy>
                                        {' '}{nearestEntrance.address}
                                        <Distance distance={distance} />
                                    </Box>
                                    {otherEntrancesCount > 0 && <Box>
                                        <HighlightedBlock color='darkMain40' borderColor='darkMain60' lineHeight='0' p='0 0.3em' display='inline-block'>
                                            <Copy as='span' lineHeight='1.2em' padding='0' fontWeight='book' size='xxSmall' color='darkMain60'>
                                                <FormattedMessage defaultMessage='Otros accesos' />
                                            </Copy>
                                        </HighlightedBlock>
                                        <HighlightedBlock color='darkMain40' borderColor='darkMain60' lineHeight='0' p='0 0.3em' display='inline-block'>
                                            <Copy as='span' lineHeight='1.2em' padding='0' fontWeight='book' size='xxSmall' color='darkMain60'>
                                                {otherEntrancesCount}
                                            </Copy>
                                        </HighlightedBlock>
                                    </Box>}
                                </ParkingAddressBlock>
                            </ParkingTextBlockHeader>
                            <Flex
                              width={['100%', 'calc(100% - 100px)']}
                              mt='8px'
                              height='36px'
                              alignItems='center'
                              justifyContent='flex-start'>
                                <ServiceIconMeasures
                                  measure={isTruckParking(parking) ? PARKING_LENGTH : PARKING_HEIGTH}
                                  value={isTruckParking(parking) ? parking.max_length : parking.max_height}
                                />
                                <Services services={parking.services} max={isTruckParking(parking) ? (MAX_SERVICES - 1) : MAX_SERVICES} />
                            </Flex>
                        </ParkingTextBlock>
                    </CardHeader>
                    <Footer>
                        { isAirport && this.renderAirportBlock() }
                        <ButtonContainer>
                            <ParkingCardButton parking={parking} buttonComponent={Button} />
                        </ButtonContainer>
                    </Footer>
                </a>
            </Card>
        )
    }
}

export default injectIntl(ParkingCard)
